(ns frontend.ui.component.lp-portal.text-content.view
  (:require
   [common.localization :as loc]
   [common.time :as c-time]
   [common.util :as cu]
   [frontend.ui.component.grid.view :as grid]
   [frontend.ui.component.lp-portal.text-content.style :as style]
   [frontend.ui.page.lp-report-editor.common.modal.edit-text-modal :as edit-text-modal]
   [frontend.utils :as fu]
   [herb.core :refer [<class] :as h]
   [reagent.core :as r]
   [reagent.impl.component :as reagent-component]
   [ui.components.button.view :as button]
   [ui.components.rich-text.tiptap :as tiptap]))

(defn rich-text-content
  [props]
  (let [{:keys [html-content completed-at]} props
        local-state (r/atom {:draft? (cu/blank-value? completed-at)
                             :current-content html-content})]
    (r/create-class
     {:display-name "rich-text-content"

      :component-did-update
      (fn [this old-argv]
        (let [new-props (r/props this)
              prev-props (reagent-component/extract-props old-argv)
              new-content (:html-content new-props)
              old-content (:html-content prev-props)
              new-completed-at (:completed-at new-props)
              old-completed-at (:completed-at prev-props)]
          ;; Update content when props change externally
          (when-not (= new-content old-content)
            (swap! local-state assoc :current-content new-content))
          ;; Update draft state when completed-at changes
          (when-not (= new-completed-at old-completed-at)
            (swap! local-state assoc :draft? (cu/blank-value? new-completed-at)))))

      :reagent-render
      (fn [{:keys [html-content completed-at complete-changed! can-edit?
                   show-draft? edit-text-label toolbar]}]
        (let [edit-mode? (:edit-mode? @local-state)
              current-content (:current-content @local-state)
              local-draft? (:draft? @local-state)
              draft? (if (boolean? local-draft?)
                       local-draft?
                       (cu/blank-value? completed-at))
              no-changes? (= html-content current-content)
              editor-empty? (and (tiptap/empty-editor-value? html-content)
                                 (tiptap/empty-editor-value? current-content))

              ;; Keep edits local while typing; persist only through explicit save actions.
              toolbar-options (merge (or toolbar tiptap/no-align-toolbar-options)
                                     {:on-change (fn [new-html]
                                                   (swap! local-state assoc :current-content new-html))})]

          [:div
           {:class (h/<class (partial style/text-container (and draft? show-draft?)))}

           ;; Draft indicator
           (when (and draft? show-draft?)
             [:div
              {:class (h/<class style/draft-indicator)}
              (loc/tr :draft)])

           [:div
            (cond
              ;; Read-only mode (no edit permission)
              (not can-edit?)
              [tiptap/viewer
               {:value current-content
                :class "min-h-24"}]

              ;; Edit mode
              edit-mode?
              [:div
               [:div
                [tiptap/editor
                 (merge toolbar-options
                        {:value current-content
                         :class "mb-4"})]]
               [:div
                {:class "flex flex-wrap gap-3 items-center"}
                [button/sm-primary-solid
                 {:on-click (fn [_]
                              (swap! local-state assoc :draft? false)
                              (complete-changed! current-content false)
                              (swap! local-state dissoc :edit-mode?))
                  :disabled? (or (and (not draft?) no-changes?)
                                 editor-empty?)}
                 "Save and complete"]
                [button/sm-primary-solid
                 {:on-click (fn [_]
                              (complete-changed! current-content true)
                              (swap! local-state dissoc :edit-mode?)
                              (swap! local-state assoc :draft? true))
                  :disabled? (or no-changes?
                                 editor-empty?)}
                 "Save and edit later"]
                [button/sm-secondary-hollow
                 {:on-click (fn [_]
                              (swap! local-state dissoc :edit-mode?)
                              ;; Reset to original content if changes were made
                              (when-not no-changes?
                                (swap! local-state assoc :current-content html-content)))}
                 "Close"]]]

              ;; View mode (can edit, but not editing)
              :else
              [:div
               [:div
                {:class (h/<class style/draft-indicator)}
                [:span
                 {:class (<class style/edit-text)
                  :on-click (fn [e]
                              (fu/stop-propagation-event! e)
                              (swap! local-state assoc :edit-mode? true))}
                 (or edit-text-label (loc/tr :lp-report-composer/edit-text))]]
               [tiptap/viewer
                {:value current-content
                 :class "min-h-24"}]])]]))})))

(def footnote-toolbar
  {:show-alignment? false
   :show-block-type? false
   :link? false
   :inline-options [:bold :italic :underline]})


(defn optional-footnote
  [{:keys [rich-content rich-content-path
           rich-content-completed-at rich-content-completed-path]}
   {:keys [edit-mode? show-draft? edit-mode-footer? save-text]}]
  (when (or edit-mode?
            edit-mode-footer?
            (and (not (cu/blank-value? rich-content))
                 (not (= (cu/str-trim-lcase rich-content) "<p><br></p>"))))
    [:div {:id (str rich-content-completed-path)}
     [grid/row {}
      [grid/column {}
       [rich-text-content
        {:html-content      rich-content
         :completed-at      rich-content-completed-at
         :complete-changed! (when (and (or edit-mode?
                                           edit-mode-footer?)
                                       (fn? save-text))
                              (fn [new-content completed?]
                                (save-text rich-content-path
                                           new-content
                                           rich-content-completed-path
                                           (if completed?
                                             ""
                                             (str (c-time/now-millis))))))
         :toolbar           footnote-toolbar
         :show-draft?       show-draft?
         :can-edit?         (or edit-mode? edit-mode-footer?)
         :edit-text-label   (loc/tr :lp-report-composer/edit-footnote)}]]]]))


#_(defn free-text-with-modal
  [{:keys [html-content completed-at completed-path complete-changed! show-draft? edit-text-label can-edit?
           on-change toolbar]}]
  (let [local-state (r/atom {:draft? (cu/blank-value? completed-at)})
        editor-state (rich/init-state html-content)]
    (r/create-class
     {:component-did-update (fn [this old-argv] ;;Force rich-editor to update its own state when props change
                              (refresh-text-editor-state
                               {:this this
                                :old-argv old-argv
                                :editor-state editor-state
                                :local-state local-state}))
      :reagent-render
      (fn [{:keys [html-content completed-at complete-changed! can-edit? on-change]}]
        (let [local-draft? (:draft? @local-state)
              draft? (if (boolean? local-draft?)
                       local-draft?
                       (cu/blank-value? completed-at))
              new-content (-> (.getCurrentContent @editor-state)
                              (stateToHTML)
                              (js->clj))
              no-changes? (= html-content new-content)
              editor-empty? (and (rich-text-v2/empty-editor-value? html-content)
                                 (rich-text-v2/empty-editor-value? new-content))]
          [:div
           {:id (str completed-path)
            :style {:scroll-margin "50px"}}
           (when (and draft? show-draft?)
             [:div
              {:class (h/<class style/draft-indicator)}
              (loc/tr :draft)])
           (cond
             (not can-edit?)
             [rich-text-editor/rich-text-editor {:read-only? true} editor-state]
             (:show-edit-modal? @local-state)
             [edit-text-modal/view
              {:header "Edit text"
               :on-hide! (fn [_]
                           (swap! local-state assoc :show-edit-modal? false)
                           (when-not no-changes?
                             (reset! editor-state @(rich/init-state html-content))))
               :on-submit! (fn [i]
                             (swap! local-state assoc :draft? (not draft?))
                             (complete-changed! new-content false)
                             (swap! local-state dissoc :show-edit-modal?)
                             (when draft?
                               (swap! local-state dissoc :edit-mode?)))
               :on-submit-edit-later! (fn [e]
                                        (complete-changed! new-content true)
                                        (swap! local-state dissoc :show-edit-modal?)
                                        (swap! local-state dissoc :edit-mode?)
                                        (swap! local-state assoc :draft? true))
               :complete-button-disabled? (or (and (not draft?) no-changes?)
                                              editor-empty?)
               :edit-later-button-disabled? (or no-changes?
                                                editor-empty?)}
              ^{:key "in-modal-editor"}
              [rich-text-editor/rich-text-editor
               {:toolbar (or toolbar
                             rich-text-editor/no-align-toolbar-options)
                :editor-in-modal? true}
               editor-state]]
             :else
             [:div
              [:div
               {:class (h/<class style/draft-indicator)}
               [:span
                {:class    (<class style/edit-text)
                 :on-click (fn [e]
                             (fu/stop-propagation-event! e)
                             (swap! local-state assoc
                                    :show-edit-modal? true
                                    :edit-mode? true))}
                (loc/tr :lp-report-composer/edit-text)]]
              ^{:key "read-only"}
              [rich-text-editor/rich-text-editor {:read-only? true} editor-state]])]))})))

(defn free-text-with-modal
  [props]
  (let [{:keys [html-content completed-at]} props
        local-state (r/atom {:draft? (cu/blank-value? completed-at)
                             :current-content html-content})]
    (r/create-class
     {:display-name "free-text-with-modal"

      :component-did-update
      (fn [this old-argv]
        (let [new-props (r/props this)
              prev-props (reagent-component/extract-props old-argv)
              new-content (:html-content new-props)
              old-content (:html-content prev-props)
              new-completed-at (:completed-at new-props)
              old-completed-at (:completed-at prev-props)]
          (when-not (= new-content old-content)
            (swap! local-state assoc :current-content new-content))
          (when-not (= new-completed-at old-completed-at)
            (swap! local-state assoc :draft? (cu/blank-value? new-completed-at)))))

      :reagent-render
      (fn [{:keys [html-content completed-at completed-path complete-changed! can-edit?
                   show-draft? edit-text-label toolbar]}]
        (let [current-content (:current-content @local-state)
              local-draft? (:draft? @local-state)
              draft? (if (boolean? local-draft?)
                       local-draft?
                       (cu/blank-value? completed-at))
              no-changes? (= html-content current-content)
              editor-empty? (and (tiptap/empty-editor-value? html-content)
                                 (tiptap/empty-editor-value? current-content))
              toolbar-options (or toolbar tiptap/no-align-toolbar-options)]

          [:div
           {:id (str completed-path)
            :style {:scroll-margin "50px"}}

           (when (and draft? show-draft?)
             [:div
              {:class (h/<class style/draft-indicator)}
              (loc/tr :draft)])

           (cond
             ;; Read-only mode (no edit permission)
             (not can-edit?)
             [:div
              {:class (h/<class (partial style/text-container false))}
              [tiptap/viewer
               {:value current-content}]]

             ;; Edit modal open
             (:show-edit-modal? @local-state)
             [edit-text-modal/view
              {:header "Edit text"
               :on-hide! (fn [_]
                           (swap! local-state assoc :show-edit-modal? false)
                           (when-not no-changes?
                             (swap! local-state assoc :current-content html-content)))
               :on-submit! (fn [_]
                             (swap! local-state assoc :draft? false)
                             (complete-changed! current-content false)
                             (swap! local-state dissoc :show-edit-modal?)
                             (when draft?
                               (swap! local-state dissoc :edit-mode?)))
               :on-submit-edit-later! (fn [_]
                                        (complete-changed! current-content true)
                                        (swap! local-state dissoc :show-edit-modal?)
                                        (swap! local-state dissoc :edit-mode?)
                                        (swap! local-state assoc :draft? true))
               :complete-button-disabled? (or (and (not draft?) no-changes?)
                                              editor-empty?)
               :edit-later-button-disabled? (or no-changes?
                                                editor-empty?)}
              ^{:key "in-modal-editor"}
              [:div
               {:class (h/<class (partial style/text-container false))}
               [tiptap/editor
                (merge toolbar-options
                       {:value current-content
                        :on-change #(swap! local-state assoc :current-content %)
                        :class "min-h-64"})]]]

             ;; View mode with edit button
             :else
             [:div
              [:div
               {:class (h/<class style/draft-indicator)}
               [:span
                {:class (<class style/edit-text)
                 :on-click (fn [e]
                             (fu/stop-propagation-event! e)
                             (swap! local-state assoc
                                    :show-edit-modal? true
                                    :edit-mode? true))}
                (or edit-text-label (loc/tr :lp-report-composer/edit-text))]]
              ^{:key "read-only"}
              [:div
               {:class (h/<class (partial style/text-container false))}
               [tiptap/viewer
                {:value current-content}]]])]))})))
